// Janela sobre o painel do pacote 1.0.0 (ver launcherStyle.js), com o comportamento das janelas do
// macOS:
// - Mover: arrastar a barra do título.
// - Redimensionar: arrastar as bordas e os cantos, até o mínimo de 360x440.
// - Expandir: o botão depois do minimizar, ou dois cliques na barra, leva o painel a 600 px de
//   largura e até 900 px de altura, crescendo a partir do canto inferior direito, e depois o devolve
//   à posição e ao tamanho de antes. Mover ou redimensionar o painel expandido não desfaz o
//   expandido: voltar retoma a caixa de antes de expandir. Com 600 px, o iframe fica abaixo dos
//   640 px em que o Flow troca para o layout largo, que aumenta a saudação. O botão vem depois do
//   minimizar porque, no celular, o pacote leva o Tab para o primeiro botão do painel.
// - Animações: ao minimizar, o painel é sugado para a logo como no efeito gênio, e a logo quica;
//   ao abrir, sai da logo com uma mola; ao expandir, muda de tamanho com a mesma mola. Com o sistema
//   pedindo menos movimento, nada anima.
//
// Tamanho e posição ficam inline. `right` e `bottom` medem a distância até o canto inferior direito
// da tela, onde o pacote ancora o painel. Enquanto ninguém mexe, vale o CSS do pacote (400x640). O
// painel fica sempre dentro da tela, com a folga de 16 px do pacote. No celular (até 640 px), o
// pacote põe o painel em tela cheia, e as alças, o arrastar e o botão de expandir somem.

const MARGIN = 16;
const MIN_SIZE = { width: 360, height: 440 };
const EXPANDED_SIZE = { width: 600, height: 900 };
const GRIPS = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

// Durante o arrasto, o iframe não recebe o mouse, para não engolir os eventos. O botão do pacote
// some com `hidden`; aqui ele fica invisível, mas com a caixa, para o painel saber de onde sair ao
// abrir. `visibility` também o tira do Tab e do leitor de tela.
export const WINDOW_STYLE = `
.bar{cursor:grab;user-select:none;-webkit-user-select:none;touch-action:none}
.bar .ttl{flex:1}
.panel[data-dragging] iframe{pointer-events:none}
.panel[data-dragging=move] .bar{cursor:grabbing}
.grip{position:absolute;z-index:1;touch-action:none}
.grip[data-dir=n],.grip[data-dir=s]{left:14px;right:14px;height:6px;cursor:ns-resize}
.grip[data-dir=e],.grip[data-dir=w]{top:14px;bottom:14px;width:6px;cursor:ew-resize}
.grip[data-dir*=n]{top:0}.grip[data-dir*=s]{bottom:0}
.grip[data-dir*=e]{right:0}.grip[data-dir*=w]{left:0}
.grip[data-dir=ne],.grip[data-dir=sw]{width:14px;height:14px;cursor:nesw-resize}
.grip[data-dir=nw],.grip[data-dir=se]{width:14px;height:14px;cursor:nwse-resize}
.size{display:inline-flex;align-items:center;justify-content:center;border:0;background:transparent;
  color:inherit;min-width:44px;min-height:44px;cursor:pointer;border-radius:8px}
.size:focus-visible{outline:3px solid var(--fc-accent);outline-offset:2px}
.size svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;
  stroke-linejoin:round}
.panel{animation:none}
.panel[hidden][data-closing]{display:flex;pointer-events:none}
.fab[hidden]{display:inline-flex;visibility:hidden}
.fab[hidden] .logo-stage{visibility:hidden}
@media (max-width:640px){.panel{width:100%!important;height:100dvh!important}.bar{cursor:auto}
  .size,.grip{display:none}}
`;

const SIZE_ICONS = {
  expand: '<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>',
  restore: '<path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7"/>',
};

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

const isPhone = () => matchMedia('(max-width: 640px)').matches;

const viewport = () => ({
  width: document.documentElement.clientWidth,
  height: document.documentElement.clientHeight,
});

/** Põe o painel na caixa dada, em coordenadas da tela, sem sair dela. */
function place(panel, { left, top, width, height }) {
  const screen = viewport();
  const w = Math.min(width, screen.width - 2 * MARGIN);
  const h = Math.min(height, screen.height - 2 * MARGIN);
  const x = clamp(left, MARGIN, screen.width - MARGIN - w);
  const y = clamp(top, MARGIN, screen.height - MARGIN - h);
  Object.assign(panel.style, {
    width: `${w}px`,
    height: `${h}px`,
    right: `${screen.width - x - w}px`,
    bottom: `${screen.height - y - h}px`,
  });
}

// Mola amortecida amostrada para o `linear()` do CSS: passa do destino, volta e assenta no fim.
// Quanto menor o amortecimento, mais ela quica.
function springEasing(damping) {
  if (!CSS.supports('animation-timing-function', 'linear(0, 1)')) {
    return 'cubic-bezier(.34,1.56,.64,1)';
  }
  const decay = 7; // e^-7: assentada no fim da animação
  const omega = decay / damping;
  const damped = omega * Math.sqrt(1 - damping ** 2);
  const points = Array.from({ length: 41 }, (_, i) => {
    const t = i / 40;
    const wave = Math.cos(damped * t) + (decay / damped) * Math.sin(damped * t);
    return i === 40 ? 1 : +(1 - Math.exp(-decay * t) * wave).toFixed(4);
  });
  return `linear(${points.join(',')})`;
}

const running = new WeakMap();

/** Anima `el`, cancelando a animação anterior dele. Com menos movimento, não anima. */
function play(el, keyframes, options) {
  running.get(el)?.cancel();
  running.delete(el);
  if (matchMedia('(prefers-reduced-motion: reduce)').matches || !el.animate) return null;
  const animation = el.animate(keyframes, options);
  running.set(el, animation);
  return animation;
}

function stop(el) {
  running.get(el)?.cancel();
  running.delete(el);
}

/** Aplica `change` e anima da caixa de antes para a nova, com a mola. */
function morph(panel, change) {
  const from = panel.getBoundingClientRect();
  stop(panel);
  change();
  const to = panel.getBoundingClientRect();
  if (!to.width || !to.height) return;
  const shift = `translate(${from.left - to.left}px,${from.top - to.top}px)`;
  const scale = `scale(${from.width / to.width},${from.height / to.height})`;
  play(
    panel,
    [
      { transformOrigin: '0 0', transform: `${shift} ${scale}` },
      { transformOrigin: '0 0', transform: 'none' },
    ],
    { duration: 560, easing: springEasing(0.6) },
  );
}

/**
 * Quadros do efeito gênio, do painel aberto até a logo: o lado do painel voltado para a logo
 * afina até a largura dela, como um funil, e o painel encolhe para dentro dela.
 */
function genie(panel, fab) {
  const box = panel.getBoundingClientRect();
  const logo = fab.getBoundingClientRect();
  const x = logo.left + logo.width / 2 - box.left;
  const y = logo.top + logo.height / 2 - box.top;
  const center = clamp(x / box.width, 0, 1) * 100;
  const half = (logo.width / box.width) * 50;
  const [a, b] = [center - half, center + half].map((v) => `${v.toFixed(2)}%`);
  // Aberto, o recorte passa 40 px da borda para não cortar a sombra.
  const out = '-40px';
  const far = 'calc(100% + 40px)';
  const open = `polygon(${out} ${out},${far} ${out},${far} ${far},${out} ${far})`;
  const funnel =
    y > box.height / 2
      ? `polygon(0% 0%,100% 0%,${b} 100%,${a} 100%)`
      : `polygon(${a} 0%,${b} 0%,100% 100%,0% 100%)`;
  const origin = `${x}px ${y}px`;
  return [
    { transformOrigin: origin, transform: 'scale(1)', clipPath: open, opacity: 1 },
    { transformOrigin: origin, transform: 'scale(.75,.9)', clipPath: funnel, opacity: 1 },
    { transformOrigin: origin, transform: 'scale(.1,.06)', clipPath: funnel, opacity: 0 },
  ];
}

/** Arrasta com o ponteiro preso em `handle`, chamando `move(caixa inicial, dx, dy)`. */
function drag(panel, event, kind, move) {
  if (event.button !== 0 || isPhone()) return;
  event.preventDefault();
  stop(panel);
  const start = panel.getBoundingClientRect();
  const handle = event.currentTarget;
  handle.setPointerCapture(event.pointerId);
  panel.dataset.dragging = kind;
  const onMove = (e) => move(start, e.clientX - event.clientX, e.clientY - event.clientY);
  handle.addEventListener('pointermove', onMove);
  handle.addEventListener(
    'lostpointercapture',
    () => {
      handle.removeEventListener('pointermove', onMove);
      delete panel.dataset.dragging;
    },
    { once: true },
  );
}

/** Liga mover, redimensionar, expandir e as animações ao painel montado. */
export function addPanelWindow(root) {
  const panel = root.querySelector('.panel');
  const bar = root.querySelector('.bar');
  const minimize = root.querySelector('.min');
  const fab = root.querySelector('.fab');
  if (!panel || !bar || !minimize || !fab) return;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'size';
  let restore = null; // caixa de antes de expandir
  const showExpanded = (expanded) => {
    panel.toggleAttribute('data-expanded', expanded);
    const label = expanded ? 'Voltar ao tamanho padrão' : 'Expandir assistente';
    button.setAttribute('aria-label', label);
    button.title = label;
    button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${SIZE_ICONS[expanded ? 'restore' : 'expand']}</svg>`;
  };
  const toggleExpanded = () => {
    if (isPhone()) return;
    morph(panel, () => {
      const { left, top, right, bottom, width, height } = panel.getBoundingClientRect();
      const expanding = !panel.hasAttribute('data-expanded');
      if (expanding) {
        restore = { left, top, width, height };
        place(panel, {
          left: right - EXPANDED_SIZE.width,
          top: bottom - EXPANDED_SIZE.height,
          ...EXPANDED_SIZE,
        });
      } else {
        place(panel, restore);
        restore = null;
      }
      showExpanded(expanding);
    });
  };
  button.addEventListener('click', toggleExpanded);
  showExpanded(false);
  minimize.after(button);

  bar.addEventListener('pointerdown', (event) => {
    if (event.target.closest('button')) return;
    drag(panel, event, 'move', (start, dx, dy) =>
      place(panel, {
        left: start.left + dx,
        top: start.top + dy,
        width: start.width,
        height: start.height,
      }),
    );
  });
  bar.addEventListener('dblclick', (event) => {
    if (!event.target.closest('button')) toggleExpanded();
  });

  for (const dir of GRIPS) {
    const grip = document.createElement('span');
    grip.className = 'grip';
    grip.dataset.dir = dir;
    grip.setAttribute('aria-hidden', 'true');
    grip.addEventListener('pointerdown', (event) =>
      drag(panel, event, 'resize', (start, dx, dy) => {
        const { width: screenW, height: screenH } = viewport();
        let { left, top, right, bottom } = start;
        if (dir.includes('w')) left = clamp(left + dx, MARGIN, right - MIN_SIZE.width);
        if (dir.includes('e')) right = clamp(right + dx, left + MIN_SIZE.width, screenW - MARGIN);
        if (dir.includes('n')) top = clamp(top + dy, MARGIN, bottom - MIN_SIZE.height);
        if (dir.includes('s')) bottom = clamp(bottom + dy, top + MIN_SIZE.height, screenH - MARGIN);
        place(panel, { left, top, width: right - left, height: bottom - top });
      }),
    );
    panel.append(grip);
  }

  // Com a tela menor, o painel que já foi mexido volta para dentro dela.
  const onResize = () => {
    if (!panel.isConnected) {
      window.removeEventListener('resize', onResize);
      return;
    }
    if (!panel.style.width || isPhone()) return;
    const screen = viewport();
    const width = parseFloat(panel.style.width);
    const height = parseFloat(panel.style.height);
    place(panel, {
      left: screen.width - parseFloat(panel.style.right) - width,
      top: screen.height - parseFloat(panel.style.bottom) - height,
      width,
      height,
    });
  };
  window.addEventListener('resize', onResize);

  // O pacote abre e minimiza trocando o `hidden` do painel. O observador roda antes da próxima
  // pintura: ao minimizar, o painel continua na tela (`data-closing`) até o fim da animação.
  let shown = !panel.hidden;
  new MutationObserver(() => {
    if (shown === !panel.hidden) return;
    shown = !panel.hidden;
    if (shown) {
      panel.removeAttribute('data-closing');
      stop(panel);
      const frames = genie(panel, fab).reverse();
      play(panel, frames, { duration: 640, easing: springEasing(0.5) });
      return;
    }
    stop(panel);
    panel.setAttribute('data-closing', '');
    // `forwards` segura o último quadro até o painel sair da tela; sem ele, o painel pisca cheio.
    const animation = play(panel, genie(panel, fab), {
      duration: 460,
      easing: 'cubic-bezier(.45,0,.7,.2)',
      fill: 'forwards',
    });
    if (!animation) {
      panel.removeAttribute('data-closing');
      return;
    }
    const done = () => {
      if (running.get(panel) !== animation) return;
      panel.removeAttribute('data-closing');
      stop(panel);
    };
    animation.finished.then(done, done);
    play(
      fab,
      [
        { transform: 'scale(.3)', opacity: 0 },
        { transform: 'scale(1)', opacity: 1 },
      ],
      {
        duration: 560,
        delay: 380,
        easing: springEasing(0.4),
        fill: 'backwards',
      },
    );
  }).observe(panel, { attributeFilter: ['hidden'] });
}
